import { Injectable, signal } from '@angular/core';

/**
 * Tracks the "splash back-lock" flag from Consumer's
 * modules/onboarding/utils/splashBackLock.ts + guestBackNavigation.ts.
 *
 * ANGULAR-NATIVE ADAPTATION (not a literal port): Consumer has to fight
 * BrowserRouter's lack of a navigation blocker with a ~180-line popstate
 * interceptor (GuestFlowPathTracker/GuestFlowLocationGuard/guestBackNavigation.ts)
 * that lets the wrong route render for a frame and then redirects — a
 * flash-then-correct pattern, documented in
 * documents/technical/02-registration-onboarding.md §6.4.
 *
 * Angular's router guards run and can redirect *before* a route ever
 * activates (`CanActivateFn` returning a `UrlTree` short-circuits rendering
 * entirely), so the equivalent protection — "once Splash has resolved
 * eligibility, browser-back must not re-enter login/onboarding/registration
 * screens" — is implemented as a guard check instead of a popstate hack. See
 * `core/guards/guest.guard.ts` and `core/guards/onboarding.guard.ts`, which
 * both consult `isSplashBackLocked()` before allowing activation.
 *
 * The registration-screen-specific "browser back while the OTP step is
 * showing rewinds to the mobile step instead of leaving the page" behavior
 * (guestBackNavigation.ts `registrationBackState`) is ported in
 * `registration.component.ts` via the Router's `NavigationStart` /
 * `navigationTrigger === 'popstate'` event instead of a `CanDeactivate`
 * guard — see that component's class doc comment for why.
 */
@Injectable({ providedIn: 'root' })
export class GuestFlowStore {
  private readonly _isSplashBackLocked = signal(false);
  readonly isSplashBackLocked = this._isSplashBackLocked.asReadonly();

  lockSplashBackNavigation(): void {
    this._isSplashBackLocked.set(true);
  }

  unlockSplashBackNavigation(): void {
    this._isSplashBackLocked.set(false);
  }
}
